import type { Ssm5RecentConnection } from '@packages/domain/ssm5';
import type { EnrichedVehicleDetails } from '@web/vin-sessions/types';

import type { Ssm5LiveConnection } from '../context/ssm5-connection.context';
import type { Ssm5ConnectionStatus } from './check-connection';

/** What the recent connections section offers, and whether Open resumes it or connects again. */
export type Ssm5ConnectionOfferState = {
    /** The connection to offer, with the vehicle it ran on. */
    connection: Ssm5LiveConnection;
    /** Whether the connection is still open, so Open shows it again instead of connecting again. */
    isConnected: boolean;
};

/**
 * Pick what the recent connections section offers: the session live in this tab when there is one,
 * else the recent connection once its vehicle is known and the backend has said whether it is still
 * open. A recent connection the backend reports gone, or could not be reached about, is offered as
 * disconnected.
 *
 * @param live - the session live in this tab, if any
 * @param recentConnection - the recent connection the backend reported, if any
 * @param vehicle - the vehicle the recent connection ran on, or undefined while unknown
 * @param recentStatus - what the status check said about the recent connection, or undefined while unchecked
 * @returns the offer, or undefined while there is nothing to offer
 */
export function toConnectionOffer(
    live: Ssm5LiveConnection | undefined,
    recentConnection: Ssm5RecentConnection | undefined,
    vehicle: EnrichedVehicleDetails | undefined,
    recentStatus: Ssm5ConnectionStatus | undefined
): Ssm5ConnectionOfferState | undefined {
    if (live) {
        return { connection: live, isConnected: true };
    }

    if (recentConnection === undefined || vehicle === undefined || recentStatus === undefined) {
        return undefined;
    }

    return { connection: { ...recentConnection, vehicle }, isConnected: recentStatus === 'open' };
}
